import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

// Utility function to create a slice and thunks dynamically
export const createApiSlice = (sliceName, apiConfig, customReducers = {}) => {
  const thunks = {};
  const extraReducers = {};

  // Generate thunks and extra reducers dynamically
  apiConfig.forEach(
    ({ name, method, url, stateKey, loadingKey, customURL }) => {
      const thunk = createAsyncThunk(
        `${sliceName}/${name}`,
        async (data, { rejectWithValue, dispatch }) => {
          try {
            const response = await axios({
              method,
              url: customURL ? `${url}/${data}` : url,
              data: method === "get" ? undefined : data,
              params: method === "get" ? data : undefined,
              withCredentials: true,
            });
            resetFunction(dispatch, slice.actions);
            return response.data;
          } catch (err) {
            resetFunction(dispatch, slice.actions);
            return rejectWithValue(err.response?.data);
          }
        },
      );

      thunks[name] = thunk;

      // Define the reducer functions
      extraReducers[thunk.pending] = (state) => {

        // custom loading key if provided
        if (loadingKey) {
          state[loadingKey] = true;
        } else {
          state.loading = true;
        }
      };
      extraReducers[thunk.fulfilled] = (state, action) => {
        // Reset custom loading key if provided
        if (loadingKey) {
          state[loadingKey] = false;
        } else {
          state.loading = false;
        }
        if (stateKey === "message") {
          state.message = action.payload?.message ?? "";
        } else {
          state[stateKey] = action.payload ?? [];
        }
      };
      extraReducers[thunk.rejected] = (state, action) => {
        // Reset custom loading key if provided
        if (loadingKey) {
          state[loadingKey] = false;
        } else {
          state.loading = false;
        }

        // Reset the data if the API throws an error
        if (stateKey !== "message") {
          state[stateKey] = [];
        }
        state.errMessage = action.payload?.message ?? "";
      };
    },
  );

  // Create the slice using createSlice
  const slice = createSlice({
    name: sliceName,
    initialState: {
      loading: false,
      message: "",
      errMessage: "",
      data: [],
      mainData: [],
      ...customReducers.initialState, // Merge custom initial state
    },
    reducers: {
      resetMessage: (state) => {
        state.message = "";
      },
      resetErrMessage: (state) => {
        state.errMessage = "";
      },
      rowSelect: (state, action) => {
        state.selectedData =
          action.payload.length > 0 ? action.payload[0] : null;
      },
      ...customReducers.reducers, // Merge custom reducers
    },
    extraReducers: (builder) => {
      // Add cases dynamically for each thunk
      Object.entries(extraReducers).forEach(([actionType, reducer]) => {
        builder.addCase(actionType, reducer);
      });
      // Add custom extra reducers
      Object.entries(customReducers.extraReducers || {}).forEach(
        ([actionType, reducer]) => {
          builder.addCase(actionType, reducer);
        },
      );
    },
  });

  return {
    slice,
    thunks,
    actions: slice.actions,
  };
};

// Helper function to reset messages after API calls
const resetFunction = (dispatch, actions) => {
  setTimeout(() => {
    actions.resetMessage && dispatch(actions.resetMessage());
    actions.resetErrMessage && dispatch(actions.resetErrMessage());
  }, 2000); // 2000 milliseconds = 2 seconds
};
